/**
 * In-App Meta: Changelog, FAQ, Wiki, Q&A-Suche
 *
 * Agent-Quelle der Wahrheit: Repo-Root `CHANGELOG.md` (Keep a Changelog).
 * Bei relevanten Spieländerungen dort nachziehen, dann hier spiegeln + APP_VERSION bumpen.
 * Siehe AGENTS.md / docs/RELEASE_NOTES.md.
 */

/**
 * Versionschema (fein): 0.MAJOR.MINOR.PATCH
 * - 0.3.x.y während Feature-Arbeit
 * - Ziel Closed Alpha / Beta um ~0.7.0.0
 * - WIP bleibt unversioniert bis Freigabe
 */
export const APP_VERSION = "0.5.4.0";

/** Noch nicht als Version freigegeben — erst ausprobieren, dann releasen */
export const WIP_NOTES = [
  "Verband / Multiplayer — Join-Modus + Amtshilfe-Stub; kein echtes MP-Gameplay",
  "Saison-Leaderboard (Multiplayer) — Solo-Board + Archiv lite",
  "JVA-Vollflow mit Fahrzeug-Transport (Gericht-Stub da)",
  "Landing: echte Map-Screenshots / Trailer (Content-Teaser live)",
  "Echte Sirenen-Samples (Proximity bleibt Synth-Stub)",
  "Asset-Kompression (Pack-PNGs → WebP) optional",
  "Siehe docs/LEVEL_STORY_GATES.md / OVERHAUL_KAMPAGNE_LEVEL.md / CHANGELOG.md",
];

/**
 * Marketing-Landing (`/`) — Features hier pflegen; bei Releases mit CHANGELOG spiegeln.
 * Spiel-Einstieg bleibt `/play`.
 */
export const LANDING = {
  brand: "BOS Dispatch Architect",
  shortBrand: "Leitstelle",
  kicker: "Private Solo-Leitstelle · Closed Alpha",
  title: "Deine Leitstelle. Echte Karte. Echte Disposition.",
  lede:
    "Baue Wachen auf OSM-Grenzen, alarmiere nach Bedarf, halte Funk und Anrufe — ohne AFK-Automation. Dark-Ops Leitstellen-Feeling, Desktop zuerst.",
  ctaPrimary: "Spielen",
  ctaSecondary: "Registrieren",
  ctaContinue: "Weiterspielen",
  nav: [
    { id: "features", label: "Features" },
    { id: "ablauf", label: "Ablauf" },
    { id: "einblicke", label: "Einblicke" },
    { id: "saison", label: "Saison" },
    { id: "entwickler", label: "Entwickler" },
  ],
  heroStates: [
    { code: "F1", label: "Wohnungsbrand", unit: "LF 10/6 · DLK · ELW", status: "Alarmiert" },
    { code: "TH", label: "VU eingeklemmt", unit: "RW · RTW · NEF", status: "Anfahrt" },
    { code: "POL", label: "Personensuche", unit: "Streife · Hubschrauber", status: "Lage" },
    { code: "RD", label: "MANV lite", unit: "KTW · KH · LNA", status: "Triage" },
  ],
  featuresTitle: "Was dich erwartet",
  featuresSubtitle:
    "Kein Idle-Clicker — echte Leitstellen-Taktik auf OpenStreetMap, mit Story-Pacing und Skills.",
  featuresHighlight: [
    {
      id: "disposition",
      tag: "Kern",
      title: "Disposition & Funk",
      body: "Sprechwünsche, Status 5, Ordnungsamt-Anrufe. Kräfte alarmieren, nachfordern, Lage halten — kritische Einsätze bleiben manuell.",
    },
    {
      id: "map",
      tag: "Taktik",
      title: "Karte & Flächendeckung",
      body: "Stadt, Landkreis oder Region auf OSM-Grenzen. Abdeckung planen, Nachbarkreis koordinieren, Bahn und JVA in der Story freischalten.",
    },
    {
      id: "campaign",
      tag: "Story",
      title: "Kampagne & Skills",
      body: "Sechs Kapitel vom ersten Zuweisungsbescheid bis zum Landesnetz. Kompetenzbaum schaltet Alarm nach Art, AAO, Semi-Auto und Autopilot frei.",
    },
  ],
  features: [
    {
      title: "Echte Verwaltungsgrenzen",
      body: "Dein Gebiet folgt OpenStreetMap — nicht einer Fantasiekarte.",
    },
    {
      title: "Kompetenz statt Idle",
      body: "Automation nur für gestartete Jobs. Ansehen zählt bei harten Lagen.",
    },
    {
      title: "Wache, Flotte, Personal",
      body: "Bauen, besetzen, ausbilden. Lehrgänge und Dienstplan wachsen mit.",
    },
    {
      title: "Saison & Ranking",
      body: "Kalender-Events, Solo-Board und saisonale Spawn-Bias.",
    },
    {
      title: "Coach & Wiki",
      body: "Eingebauter Coach führt durch Kapitel und Systeme — ohne Spoiler-Overload.",
    },
    {
      title: "Closed Alpha",
      body: "Solo-Leitstelle, Desktop zuerst. Feedback willkommen, kein Discord-Zwang.",
    },
  ],
  howTitle: "So spielst du",
  howSubtitle: "Drei Schritte vom Konto zur ersten Lage — Login ist der Einstieg.",
  howSteps: [
    {
      step: "01",
      title: "Konto",
      body: "Anmelden oder registrieren. Login zuerst — die Stadtauswahl kommt danach im Spiel.",
    },
    {
      step: "02",
      title: "Gebiet wählen",
      body: "Stadt oder Landkreis festlegen. Grenzen und OSM-BOS-Punkte kommen automatisch mit.",
    },
    {
      step: "03",
      title: "Disponieren",
      body: "Leitstelle bauen, erste Wache setzen, alarmieren. Coach und Kampagne führen Schritt für Schritt.",
    },
  ],
  screensTitle: "Einblicke",
  screensSubtitle: "Platzhalter-Grafiken — echte Screenshots folgen in einer späteren Version.",
  screens: [
    {
      id: "map",
      title: "Taktische Karte",
      caption: "OSM-Grenzen, Wachen, Einsatz-Pins und Abdeckungsradius.",
    },
    {
      id: "dispatch",
      title: "Disposition",
      caption: "Einsatzliste, Alarmierung, Funk und Sprechwünsche in einem Dock.",
    },
    {
      id: "station",
      title: "Wache & Personal",
      caption: "Fuhrpark, Besatzung, Lehrgänge und Dienstplan pro Standort.",
    },
  ],
  seasonTitle: "Saison & Community",
  seasonBody:
    "Kalender-Saisons mit Solo-Ranking, Archiv und saisonalem Spawn-Bias — auch ohne Multiplayer. Verband und echtes MP sind in Vorbereitung.",
  seasonBullets: [
    "Solo-Leaderboard pro Saison",
    "Saisonale Einsatz-Schwerpunkte",
    "Archiv vergangener Events",
    "Verband-Prep (Amtshilfe-Stub)",
  ],
  developerTitle: "Entwickler",
  developerName: "Black",
  developerNote:
    "Indie-Simulation aus Leidenschaft für Leitstellen-Taktik — kein Publisher, kein Live-Service-Zwang.",
  incidentsTitle: "Typische Lagen",
  incidentsTeaser: [
    { code: "F1", label: "Wohnungsbrand", hint: "Atemschutz · DLK" },
    { code: "TH", label: "VU eingeklemmt", hint: "RW · RTW" },
    { code: "RD", label: "MANV lite", hint: "Triage · KH" },
    { code: "POL", label: "Personensuche", hint: "Streifen · Funk" },
    { code: "ABC", label: "Gefahrgut", hint: "Messung · Absperrung" },
    { code: "GR", label: "Großlage", hint: "Abschnitte · Stab" },
  ],
  authTitle: "Konto",
  authSubtitle: "Anmelden oder registrieren — danach geht es direkt ins Spiel.",
  footNote: "Alpha · Desktop zuerst · kein Discord",
} as const;

export const CHANGELOG = [
  {
    version: "0.5.4.0",
    date: "2026-08-24",
    notes: [
      "Landing komplett neu: Scroll-Sections, Hero-Visual, Sticky-Header mit Anmelden/Registrieren/Spielen",
      "Features, Ablauf, Einblicke, Saison-Teaser, Entwickler — Texte in content.ts (LANDING)",
      "Scroll-Reveals, animierte Map-Atmosphäre, Auth-Modal — Mobile responsive",
    ],
  },
  {
    version: "0.5.3.0",
    date: "2026-08-24",
    notes: [
      "Produktions-Domain: bos-dispatch.de (www) mit HTTPS — gleiche Leitstelle wie bisher",
      "Test-URL leitstelle.*.nip.io bleibt als Fallback; Lost Paradise getrennt",
    ],
  },
  {
    version: "0.5.2.0",
    date: "2026-08-24",
    notes: [
      "Marketing-Landing: Map-Atmosphäre, Feature-Grid, Ablauf, Einsatz-Teaser — Texte in content.ts (LANDING)",
      "nginx: leitstelle.*.nip.io liefert nur Leitstelle (/var/www/leitstelle/public); Lost Paradise Apps unverändert getrennt",
      "Deploy prüft per curl Marken-Strings (BOS / Dispatch), nicht Lost Paradise",
    ],
  },
  {
    version: "0.5.1.0",
    date: "2026-08-24",
    notes: [
      "Eigene Startseite: Hero, Anmelden/Registrieren/Spielen — Login zuerst, nicht Register",
      "Nach Login: Spielstand wählen (Multi-Slot) oder direkt weiterspielen",
      "Passwort vergessen von der Landing-Anmeldung; Link Rechtliches (LEGAL.md)",
      "Perf: Spiel-Bundle lazy; alte erledigte Einsätze werden serverseitig ausgedünnt",
    ],
  },
  {
    version: "0.5.0.0",
    date: "2026-08-24",
    notes: [
      "Kampagne/Pacing-Overhaul: Personal & Alarm früh, Karten-Taktik nach Flächendeckung, Nachbar→Bahn→JVA, Verband-Prep mit Bahn",
      "Kompetenzbaum: Alarm nach Art ab Lv4, AAO Lv6, Semi-Auto Lv8, Autopilot Lv14 (Malus bleibt)",
      "Lehrgangs-Grace unverändert — Toast „Ausbildung zählt“ wenn Gates hart werden",
      "JVA erst nach Nachbar-Story (oder Lv18); Coach/Wiki an neues Pacing angepasst",
    ],
  },
  {
    version: "0.4.32.0",
    date: "2026-08-24",
    notes: [
      "Coach: Kap. 5/6 Fortschritt (Nachbar-Wache, Bahn, Verband) erkennt Bedingungen korrekt",
      "Autopilot: Auto-% in der Top-Leiste + Toast bei Soft-Malus (−Ansehen)",
      "Einsätze: Filter Schwere, Hotkeys 1–9 und ?-Hilfe",
      "Personal: „Dienstplan sichern“ (Frei→Dienst + Mindestbesatzung)",
      "Finanzen: Image-Kampagne — Ansehen ausgeben für bessere Bewerber",
      "Saison-Archiv: Leerzustand-Hinweis; FAQ Tastatur/Ansehen",
    ],
  },
  {
    version: "0.4.31.0",
    date: "2026-08-24",
    notes: [
      "Fix: Alarmierung nach erster Wache — Lehrgangs-Gates soft bis Level 5 und ch1 Personal/Fuhrpark (Maschinist blockierte Early Game)",
      "Basis-LF braucht keinen Maschinisten; DLK/TLF/HLF weiter mit Gate nach Grace",
      "Saison: Archiv abgeschlossener Saisons + Pause-Hinweis im Ranking-Panel",
      "Verband: Amtshilfe-Anfrage-Stub; KH-Hub: Auslastung % und deutsche Fachrichtungen",
    ],
  },
  {
    version: "0.4.30.0",
    date: "2026-08-24",
    notes: [
      "Verband: Join-Modus und Name speichern sich in der Stub-Tabelle (kein Multiplayer)",
      "Admin: Saison-Vorschau (z. B. Halloween außerhalb Oktober) oder Env SEASON_PREVIEW",
      "Interim Level-/Story-Gates dokumentiert (docs/LEVEL_STORY_GATES.md) — Rebalance später",
      "Wiki/FAQ: Spielstand-Slots und JVA-Gericht kurz nachgezogen",
    ],
  },
  {
    version: "0.4.29.0",
    date: "2026-08-24",
    notes: [
      "Multi-Slot-Picker: ab maxSlots > 1 (Admin) — anlegen, wechseln, einzelnen Slot löschen; Default bleibt 1",
      "Saison-Ranking-Board Lite: Banner klickbar, eigene Punkte + Regel-Tabelle",
      "JVA: Gerichtstermin-Stub und manuelle Entlassung (zeitgebunden, ohne Fahrzeug-Transport)",
    ],
  },
  {
    version: "0.4.28.0",
    date: "2026-08-24",
    notes: [
      "Saison: Ambient-Einsätze folgen dem Kalender (Sommer = mehr Baden/Grill/Waldbrand u. ä.)",
      "Bahn-Transfer: Fahrzeug-Marker wandert auf der Karte Terminal→Terminal",
      "E-Mail: echter SMTP-Versand wenn SMTP_* gesetzt (sonst weiter Admin-Hinweis)",
      "Verband-Seite: Join-Modus und Mitglieder-Cap (Stub, noch kein Multiplayer)",
    ],
  },
  {
    version: "0.4.27.0",
    date: "2026-08-24",
    notes: [
      "Bahn-Polish: nur Ziele in der anderen Zone, ETA in der Liste, klarere Terminal-Hinweise",
      "Saison spielbar: Kalender-Bonus auf Vergütung/XP, Spawn-Bias, Banner auf der Karte",
      "Nachbar-UX: Label gesperrt/aktiv + Level-Hinweis; Bau-Fehler mit Unlock-Text",
      "Story: Kap. 5 Bahn-Mission; Kap. 6 Landesnetz + Verbandsvorbereitung",
      "JVA: manuell Polizei-Zelle → JVA verlegen; Verband-Stub Cap/Join-Helfer; docs/SMTP.md",
    ],
  },
  {
    version: "0.4.26.0",
    date: "2026-08-24",
    notes: [
      "Bahn-Transfer Lite: Güterbahnhof + Modul Bahnanschluss — Fahrzeuge Heimat ↔ Nachbarkreis mit km-Fahrzeit (kein Teleport)",
      "Genau ein Nachbarkreis (kein Multi-Ring); Karten-Hinweis Level 12",
      "Saison-Stub: Sommer/Halloween-Events + Ranking-Punkte-Tabelle (Platzhalter)",
      "Verband-Konzept: 3 Schichten klar (lokal / Großlage / Bahn), kein HQ, Cap upgradebar, Join ohne Welt-Default",
    ],
  },
  {
    version: "0.4.25.0",
    date: "2026-08-24",
    notes: [
      "Nachbarkreis: Unlock ab Level 12 / Story; Bau im Nachbar erlaubt (Skills bleiben, Wachen neu)",
      "Story: THW früher in Kap. 3; Kap. 4 Amtshilfe-Nachbar; Kap. 5 Wache/Flotte/Schule/Lage",
      "Spielstände: Admin/Env können Slot-Limit anheben (Default 1, getrennt)",
      "Passwort vergessen: API-Stub + SMTP_*-Hooks (ohne SMTP = Admin-Hinweis)",
      "Konzept Verband: docs/KONZEPT_VERBAND.md (3-Schichten-Modell)",
    ],
  },
  {
    version: "0.4.24.0",
    date: "2026-08-24",
    notes: [
      "Story Kap. 3–4: Coach-Guides; RW-Bogen nach Rathaus/IM; Mission JVA (ch4_jva)",
      "Nachbarkreis auf derselben Karte (teal Grenze, Label); Spawn ab Level 8; Cross-Alarm",
      "JVA ab Level 16 + Gewahrsam-Praxis: Überlauf aus Polizei-Zellen, Tageslage lite",
      "Spielstand erklärt: 1 Slot in Einstellungen/Stadtauswahl (kein Picker)",
      "Konzept-Pack: docs/KONZEPT_ENTSCHEIDUNGEN.md mit Fragen (Auth, Verband, Slots)",
    ],
  },
  {
    version: "0.4.23.0",
    date: "2026-08-24",
    notes: [
      "Save-Slots API: maxSlots/activeWorldId — UI bleibt 1 Slot (Alpha), Multi-Slot vorbereitet",
      "Autopilot Soft-Malus: zu hoher Auto-Anteil kostet Ansehen (Caps bleiben)",
      "Story Kap. 2: Rathaus-Angebot & IM-Fuhrpark; Pol/RD-Lagen „Drei“ = 3",
      "MANV Triage lite: Patienten Rot/Gelb/Grün sortieren (Button-Fallback bleibt)",
      "Passwort vergessen: Hinweis ohne E-Mail — Admin/Einstellungen",
      "Krankenhaus Tageslage: Betten/Warteschlange/Überlast-Tipp",
      "Wiki Gate-Artikel länger; LEGAL.md Platzhalter-Draft; Nähe-Sirene dokumentiert",
    ],
  },
  {
    version: "0.4.22.0",
    date: "2026-08-24",
    notes: [
      "OSM-BOS-Punkte: Feuerwache/Polizei/KH als SVG-Icons statt Textkürzel auf der Karte",
      "Tablet Disposition: Alarm-Tab Buttons volle Breite, bessere Touch-Ziele im Einsatz-Sheet",
      "Script scripts/regenerate-wiki-seed.mjs — Wiki-Seed-Gates an Runtime-Inferenz angleichen",
    ],
  },
  {
    version: "0.4.21.0",
    date: "2026-08-24",
    notes: [
      "Fehlbestand Story: ab 2. Hinweis Nachverfolgung/Audit (4. Dialogschritt)",
      "Geplante Einsätze: unvollständige Prep-Checkliste → −Ansehen bei Freigabe oder Auto-Start",
      "Wiki-Gates: Lehrgang-Artikel ausführlicher (Atemschutz, GF, Pol, RD, Maschinist …)",
    ],
  },
  {
    version: "0.4.20.0",
    date: "2026-08-24",
    notes: [
      "Passwort ändern in Einstellungen (aktuelles + neues Passwort, min. 8 Zeichen)",
      "Kompetenz hr_recruiter: Personalwerber an ILS — Auto-Aushang alle ~48 Min (halber Preis)",
      "Spielstand: 1 Slot sichtbar — Weiterspielen auf Stadtauswahl, Hinweis in Einstellungen",
    ],
  },
  {
    version: "0.4.19.0",
    date: "2026-08-24",
    notes: [
      "Semi-Auto / Autopilot: Warn-Chip im Einsatz wenn Parallel-Cap voll (2 bzw. 3 Kleinlagen)",
      "Wartende Kleinlage zeigt Cap-Hinweis statt stiller Queue",
    ],
  },
  {
    version: "0.4.18.0",
    date: "2026-08-24",
    notes: [
      "Lageboard Stabs aktiv: Abschnitts-Chips öffnen Karte mit Werkzeug; Disposition bei Kräftebedarf offen",
      "Feuerwehrschule in ≤8 km: −20 % Lehrgang (stapelt mit crew_train −20 %)",
      "Hotkey D: AAO Maximalbedarf — Hotkey A: Tab Alarm/Disposition",
      "Lehrgang-Buttons zeigen Rabatt-Kosten (−20 % je Bonus)",
    ],
  },
  {
    version: "0.4.17.0",
    date: "2026-08-24",
    notes: [
      "Fehlbestand: 3-Schritt-Dialog — Anruf, IM/Rathaus-Briefing, Maßnahmen (unterschiedliche Texte je Amt)",
      "Hotkey D: Disposition/Alarm auch bei aktivem Einsatz (Nachalarm) — ergänzt A",
      "Einsatzliste: Badge zeigt Anzahl aktiver Filter",
    ],
  },
  {
    version: "0.4.16.0",
    date: "2026-08-24",
    notes: [
      "Wiki-Gates Voll-Remap: Lehrgänge aus Fahrzeugbedarf + Titel für fast alle Katalog-Einsätze — Wiki-Stubs im Gate-Chip",
      "pol_dgl_gross: Gewahrsam-Standard auto auf Großlagen (DGL-Chip); custody_auto auf Kleinlagen",
      "Vor Alarm: Kräftebedarf-Vorschau im Tab Alarm bei Lage/MANV (Bedarf + Stabs-Hinweis)",
      "Lehrgangsnetz (crew_train): −20 % Lehrgangskosten serverseitig",
    ],
  },
  {
    version: "0.4.15.0",
    date: "2026-08-24",
    notes: [
      "Einsatz-Gates erweitert: Excel-Katalog trainings (Atemschutz, Boot) + Fahrzeug-Gates — Wiki-Lehrgang-Stubs im Gate-Chip",
      "GF Kleinlagen (gf_kleinlagen): Gruppenführer/ELW entscheidet Standard-Taktik vor Ort auf Kleinlagen — Chip GF im Banner",
      "Alarmierung nach Art serverseitig vervollständigt: Auto-ETA-Nachalarm protokolliert Typ/ETA wie Semi-Auto/Autopilot",
      "Geplante Einsätze: Countdown mm:ss, Startfenster-Warnung wenn Termin erreicht, Prep-Checkliste unverändert kosmetisch",
      "Hotkey A: offenen Einsatz direkt im Tab Alarm (Disposition) — Q bleibt Funk",
    ],
  },
  {
    version: "0.4.14.3",
    date: "2026-08-24",
    notes: [
      "Fix: Login mit Benutzername funktioniert unabhängig von Groß-/Kleinschreibung (z. B. „black“ → „Black“)",
      "Anmeldemaske startet standardmäßig auf „Anmelden“ statt Registrierung",
    ],
  },
  {
    version: "0.4.14.2",
    date: "2026-08-24",
    notes: [
      "Fix: Bluescreen beim Reload — Wachen-Übersicht (Coach öffnet „Wachen“) hatte einen React-Hooks-Fehler",
      "Fehler-Oberfläche zeigt jetzt auch die Komponenten-Spur für schnellere Diagnose",
    ],
  },
  {
    version: "0.4.14.1",
    date: "2026-08-24",
    notes: [
      "Fix: Reload-Bluescreen — sicheres Parsen von Einsatz-JSON (requiredJson/supportJson) in Liste und Karte",
      "Fehler-Oberfläche statt leerem Bildschirm, wenn die UI doch abstürzt (Hinweis + Neu laden)",
    ],
  },

  {
    version: "0.4.14.0",
    date: "2026-08-24",
    notes: [
      "Einsatzkatalog: 432 realistische Einsätze aus neuer Excel-Liste — alte Wiki-/Handliste ersetzt",
      "MANV, Lage, Wasser, Ausbreitung und Wachen-Gates aus Leitstellenspiel-Daten abgebildet",
      "Fix: Reload-Bluescreen bei ungültigem Einsatz-supportJson",
    ],
  },

  {
    version: "0.4.13.0",
    date: "2026-08-24",
    notes: [
      "Auto-ETA Härte: Nachalarm-Cap (2 Wellen), Countdown und −Ansehen wenn Vorschlag ignoriert (alarm_auto_eta)",
      "Stabs Phase 3 lite: Lageboard-Button öffnet Tab Karte mit nächster Pflicht-Maßnahme (Abschnitt, Evakuierung …)",
      "ELW Funk-Assist (elw_funk_auto): Gruppenführer/ELW quittiert Standard-Sprechwünsche vor Ort automatisch",
      "Einsatz-Gates Start: fehlende Ausbildung blockiert Alarmierung — Hinweis im Einsatz-Banner + Wiki-Verweis",
    ],
  },

  {
    version: "0.4.12.0",
    date: "2026-08-24",
    notes: [
      "Ereignis-Editor Live-Spawn: Status „ready“ — neue Editor-Events spawnen in der Welt (Welt/Bürger/Anruf), Folgeeinsätze optional geplant",
      "Freigegebene Editor-Einsätze: selten als geplante oder offene Lage (nur approved, kein Wiki-Seed)",
      "Lageboard Phase 2: Abschnitts-Vorschau, Kräftebedarf-Zeile, Filter „Kräftebedarf offen“",
      "Autopilot (auto_pilot): Kleinlagen nach ~1 min Grund-AAO, max. 3 parallel — Fehlalarm bei Verfall −Ansehen",
      "Funk-Hotkey Q: obersten Sprechwunsch quittieren oder Einsatz öffnen",
      "Einsatzliste: Quelle „Editor“ für Custom-Spawns",
    ],
  },

  {
    version: "0.4.11.0",
    date: "2026-08-24",
    notes: [
      "Semi-Auto AAO: Kleinlagen ohne Alarm nach Timeout → Grund-AAO (Kompetenz auto_semi, max. 2 parallel)",
      "Alarmierung nach Art: Server zeigt welche Typen alarmiert wurden und warum (schnellste ETA)",
      "MANV: transportierte Patienten gehen ins Krankenhaus (Bett oder Warteschlange)",
      "Stabs Hinweis vor Alarm bei Lage/MANV — offene Karten-Maßnahme im Banner",
      "Funk-Stapel: Warnung bei mehreren Sprechwünschen — Countdown bis Ansehen-Malus",
    ],
  },

  {
    version: "0.4.10.0",
    date: "2026-08-24",
    notes: [
      "Stabsraum Phase 4: Stabs-Phase im Einsatz-Banner, Disposition und Karten-Toolbar — nicht nur Lageboard",
      "MANV Patientenfluss: Warteschlange nach Triage + Sammelstelle — Zähler in Taktik und Lageboard",
      "Geplante Einsätze: Prep-Checkliste mit Karten-Maßnahmen (Abschnitt, Evakuierung, Patienten-Sammelstelle); Countdown in der Liste",
      "Auto-ETA Nachalarm: bei langsamer Anfahrt oder offenem Bedarf ergänzt das System Kräfte (Kompetenz alarm_auto_eta)",
    ],
  },

  {
    version: "0.4.9.0",
    date: "2026-08-24",
    notes: [
      "Stabsraum Phase 3: Lageboard zeigt Stabs-Phasen je Karten-Maßnahme (Abschnitt → Evakuierung → Einheit bzw. MANV Sammelstelle)",
      "MANV Busunfall: Taktik Triage (rd_triage) erscheint wenn RTW vor Ort — Standard für rd_manv_bus",
      "Hydranten-Druckmangel bei Brand: Karte zoomt automatisch auf Einsatz + nächsten Hydrant",
      "Lageboard-Filter „Karten-Maßnahme fehlt“ für aktive Lagen mit offenen Pflicht-Maßnahmen",
    ],
  },

  {
    version: "0.4.8.0",
    date: "2026-08-24",
    notes: [
      "Lageboard: Karten-Maßnahmen für Lage- und MANV-Einsätze — Abschnitt, Evakuierung, Patienten-Sammelstelle sichtbar",
      "MANV Triage: Entscheidung verknüpft mit Patienten-Sammelstelle — Bonus-Progress und Hinweise",
      "Hydranten-Druckmangel bei Brand: Karte zeigt Banner und nächsten Hydrant (unbrauchbar) — Saugstelle/Pendel im Wasser-Tab",
      "Coach ch1_taktik: Hinweise für MANV/RD-Profil",
    ],
  },

  {
    version: "0.4.7.0",
    date: "2026-08-24",
    notes: [
      "MANV / RD: neues Karten-Profil rd_staging — Patienten-Sammelstelle, Abschnitte, bis Sammelstelle halber Alarm",
      "Einheit postieren: Streife vor Ort wählen statt automatisch nächstes Fahrzeug",
      "Hydranten-Druckmangel (~15 %): auch bei Brandlagen mit Wasser-Hold und waterRisk — Wasser-Panel blockiert Schlauch verlängern",
      "Verfolgung: Verkehrskontrollpunkt auf Route verlangsamt Flüchtigen leicht",
    ],
  },

  {
    version: "0.4.6.0",
    date: "2026-08-23",
    notes: [
      "Hydranten-Druckmangel: bei ~15 % der Wasser-Einsätze kein Hydrant — Saugstelle oder TLF-Pendel nötig",
      "Evakuierungsbereich und Abschnittsgrenze als Kreis auf der Karte sichtbar",
      "THW Wasserrettung: eigenes Karten-Profil mit Sammelstelle, Abschnitt und Saugstelle",
      "Coach ch1_taktik: Hinweise passend zum Einsatzprofil",
    ],
  },

  {
    version: "0.4.5.0",
    date: "2026-08-23",
    notes: [
      "Karten-Taktik: fünf Profile — Verfolgung, Großlage, Lageführung, Wasser, Absperrung",
      "Lage-Einsätze: Sammelstelle, Abschnitt, Sperre, Evakuierungsbereich, Einheit postieren",
      "Wasser-Einsätze: Hydrant oder Saugstelle direkt auf der Karte — löst Wasser-Hold",
      "VU/BAB: Sperrstellen und Verkehrskontrollpunkte — halber Alarm bis erste Sperre",
      "Toolbar zeigt Werkzeuge passend zum Einsatzprofil",
    ],
  },

  {
    version: "0.4.4.0",
    date: "2026-08-23",
    notes: [
      "Einstellungen / Optik: Nähe-Sirene ein- und ausschalten (Proximity-SFX bei Zoom nahe Einsatz)",
      "Bau- und Budget-Fehler: Toast 14 s — in Dev detaillierte Budget-Hinweise von der API",
      "Lage-Einsätze: Abschnittsgrenze auf der Karte markieren (Stabsraum-Vorbereitung)",
      "Geplante Einsätze: Prep-Checkliste mit Häkchen (Streife, Sammelstelle, NEA, Verkehr)",
      "Ereignis-Editor: Spawn-Felder dokumentiert · phoneKind-Hinweis bei Anrufen",
      "Fuhrpark-Shop: Mindestbesatzung sichtbar · ELW-Hinweis ab Level 6 an der Feuerwache",
      "Wiki: Welt-Ereignisse-Stub · Auto-Wiki mit Kripo, KTU, NEA, Pol-Heli",
    ],
  },

  {
    version: "0.4.3.0",
    date: "2026-08-23",
    notes: [
      "Einsatzliste: Filter nach Status, Organisation und Quelle (Chips unter den Lanes)",
      "Lageboard: Phasen offen/aktiv/geplant, Unterbesetzung, Klick öffnet Einsatz (Wachübersicht + Lagezentrum)",
      "Fehlbestand: IM/Rathaus-Dialog in zwei Schritten mit Fuhrpark- und Petitions-Shortcuts",
      "Funk: Sprechwunsch länger ignoriert → einmalig −1 Ansehen (ab ~4 Minuten)",
      "Cap-Badge zählt nur parallele Lagen (offen+aktiv); geplant separat",
    ],
  },

  {
    version: "0.4.2.0",
    date: "2026-08-23",
    notes: [
      "Verfolgung: Route über Straßennetz (OSRM/OSM) — nicht mehr Luftlinie",
      "Einheit postieren bindet Streife vor Ort; Toast wenn Flüchtiger entkommen (Malus erklärt)",
      "Geplante Einsätze: Countdown, Checkliste; Volksfest ~1 h nach Rathaus-Zusage",
      "Volksfest-Sicherheitslage mit Kräfte-Sammelstelle; Feldbrand am See; BAB-Verfolgung",
      "Alarmierung nach Art + Auto-ETA bei AAO — serverseitig, schnellste Einheit je Typ",
      "Fehlbestand: IM/Rathaus-Anruf + Petition Sondermittel Fuhrpark",
      "Coach ch1_taktik: geführte Kette Einsatz → Karte → Werkzeug",
      "Bauen: NEA-Modul-Hinweis ab Level 6",
    ],
  },

  {
    version: "0.4.1.0",
    date: "2026-08-23",
    notes: [
      "Einsatzführung auf der Karte: Verfolgung mit Nagelgurt, Sperre, Heli-Korridor — Flüchtiger bewegt sich auf der Route",
      "Großlagen: Kräfte-Sammelstelle auf der Karte — bis dahin halber Alarmierungs-Umfang",
      "Einsatz-Tab Karte · Toolbar auf der Karte · Pol-Hubschrauber startet Luftführung",
      "Wasser: Hydranten-Layer umschalten, Saugstelle per Klick auf See/Fluss",
      "ELW/GF mit Kompetenz elw_funk_auto löst einfache Taktik automatisch",
      "Coach-Auftrag: Einsatz auf der Karte (ch1_taktik)",
    ],
  },

  {
    version: "0.4.0.0",
    date: "2026-08-23",
    notes: [
      "Disposition neu sortiert: Funk (Sprechwunsch), Taktik, Wasser und Anfordern haben eigene Tabs",
      "Taktik vor Ort: Entscheidungen bei fehlender Führung — FW/Pol/RD mit Zeit-Tradeoff, Auszahlung bleibt",
      "Wasser-Panel: Schlauch, Saugstelle, TLF-Pendel — ohne Funk-Sprechwunsch",
      "Mehr Nachforderungen: Stadtwerke, THW, Krisenintervention, Betreuung, NEA, Kripo, KTU, Pol-Hubschrauber u. a.",
      "Abschlepper und Öl kommen nicht mehr als Sprechwunsch — direkt unter Anfordern",
    ],
  },

  {
    version: "0.3.53.0",
    date: "2026-08-23",
    notes: [
      "Kampagnen-Bescheide: während Übe-Aufträgen (Funk, Org-Nachweis …) keine fremden Verträge mehr — Rahmenvertrag nicht mehr nur nach sechs Missionen",
      "Coach: Tour und „Neuer Auftrag“ warten, solange ein Bescheid offen ist; Leitstelle wird überall gleich erkannt",
      "Einsätze: vorgemerkte Fahrzeuge (Warteschlange) verhindern Verfall — kein Verschwinden mehr bei „in Bearbeitung“",
      "Neue Entwickler-Referenz docs/KONZEPT_AKTUELL.md — was im Spiel wirklich implementiert ist",
    ],
  },

  {
    version: "0.3.52.0",
    date: "2026-08-23",
    notes: [
      "Grafikset Münster BF: Polizei NRW — Streifenwagen S-Max, Vito DGL, FüKW, DHuFüKW, Motorrad, GruKw, GefKw, LeBefKw, MEK-MTF mit Blaulicht",
      "Polizeifahrzeuge haben eigene Sprites pro Typ (DGL, BAB, Motorrad, …) statt nur generischem FuStW",
    ],
  },

  {
    version: "0.3.51.0",
    date: "2026-08-23",
    notes: [
      "Besatzung 2. FZ: Server füllt Fahrzeuge nicht mehr heimlich — Auftrag endet erst nach Ihrer Zuweisung",
      "Flächenförderung: Bescheid erst nach Personal-Kette und Nachweis — kein Sprung mehr während Besatzung",
      "Kampagnen-Bescheide: während offener Führungs-Aufträge gesperrt (BMI beim Fuhrpark weiter erlaubt)",
    ],
  },

  {
    version: "0.3.50.0",
    date: "2026-08-23",
    notes: [
      "Wache befüllen: unterbesetzte Fahrzeuge zuerst bis Mindestbesatzung — nicht mehr der erste LF mit freien Sitzen",
      "Besatzung: klare Toasts (Erfolg/Fehler), Intro und Hinweise bei Pool + Unterbesetzung",
      "Manuelle Zuweisung bestätigt per Toast; Coach Besatzung 2. FZ in einfachen Schritten",
    ],
  },

  {
    version: "0.3.49.0",
    date: "2026-08-23",
    notes: [
      "Besatzung: Tab wählt automatisch die Einsatzwache mit Fahrzeugen — Leitstelle erscheint nicht mehr im Dropdown",
      "Coach Besatzung: zuerst Wache-Dropdown wenn falsch gewählt, dann Wache befüllen — Tooltip blockiert den Button nicht",
      "Kampagne: Unterbesetzungs-Check zählt nur Fahrzeuge an Einsatzwachen",
    ],
  },

  {
    version: "0.3.48.0",
    date: "2026-08-23",
    notes: [
      "Coach in Verwaltung: Führung markiert Personal/Wachen/Bauen in der Tab-Leiste oben — nicht mehr den Dock-Button unten",
      "Spotlight blockiert keine Klicks mehr — Zuweisen, Wache befüllen und Tabs bleiben bedienbar",
      "Cyan-Ring sitzt außerhalb des Ziels, ohne Button-Fläche zu überdecken",
    ],
  },

  {
    version: "0.3.47.0",
    date: "2026-08-23",
    notes: [
      "Personal-Modal lässt sich wieder schließen (×, Esc, Schließen) — Coach öffnet es nicht mehr automatisch erneut",
      "Coach-Spotlight: dicker cyan Ring über Modals und Dock — Führung in Personal/Werben/Besatzung endlich sichtbar",
      "Neuer Kampagnen-Schritt: nach Einstellung Besatzung dem zweiten Fahrzeug zuweisen",
    ],
  },

  {
    version: "0.3.46.0",
    date: "2026-08-23",
    notes: [
      "Kampagnen-Bescheide: kein doppeltes Unterschreiben mehr während Erste Lagen und Personal — Verträge nur im passenden Auftrag",
      "Coach in Modals: Wachen- und Personal-Panels bleiben hell, Spotlight markiert Klickziele im geöffneten Panel",
      "Tablet: Wachen/Fahrzeugshop mit fixierter Schließen-Leiste — Panel lässt sich wieder verlassen",
    ],
  },

  {
    version: "0.3.45.0",
    date: "2026-08-23",
    notes: [
      "Blaulicht: auf der Rückfahrt zur Wache keine Blaulicht-Grafik mehr — nur bei aktivem Einsatz (Anfahrt, vor Ort, Transport, Gewahrsam)",
      "Auffüllen: TLF/LF mit Tank können an der Heimatwache befüllt werden — sichtbarer Tank-Fortschritt wie am Hydranten",
      "Grafikpacks: Anleitung für zwei Sprites pro Typ (normal + -bl) im Wiki unter Fahrzeug-Optik",
    ],
  },

  {
    version: "0.3.44.0",
    date: "2026-08-23",
    notes: [
      "Aufbauplan: Klick auf Budget/Ansehen/Einfluss/Level während der Einführung öffnet kein Vertrags-Modal mehr",
      "Coach-Panel: Text per ⤢ vergrößern, ziehbar und scrollbar — auf Tablet und PC voll lesbar",
      "Bau-Aufträge: Führung markiert nur noch „Bauen“ unten in der Leiste — kein „Bauen öffnen“ im Coach",
      "Spotlight: sauberer pulsierender Ring ohne doppelte Umrandung",
    ],
  },

  {
    version: "0.3.43.0",
    date: "2026-08-23",
    notes: [
      "Blaulicht auf der Karte: mit Grafik-Pack (z. B. Münster) nur noch die animierte Blaulicht-Grafik — keine extra CSS-Punkte mehr",
      "Unterwegs, vor Ort und auf Rückfahrt: Pack-Blaulicht-Variante; an der Wache normale Grafik ohne Blaulicht",
    ],
  },

  {
    version: "0.3.42.0",
    date: "2026-08-23",
    notes: [
      "Kampagnen-Bescheide: nur noch einer gleichzeitig — nächster erst nach Unterschrift und kurzer Pause",
      "Nach Unterschrift kein extra „Weiter“-Schritt mehr — Modal schließt sofort, Belohnung als Toast",
    ],
  },

  {
    version: "0.3.41.0",
    date: "2026-08-23",
    notes: [
      "Grafik-Pack Münster: Fahrzeuge auf Karte und in Listen deutlich größer (~35 %)",
      "Blaulicht wie im Original: ohne Blaulicht an der Wache, mit Blaulicht unterwegs, vor Ort und auf Rückfahrt",
    ],
  },

  {
    version: "0.3.40.0",
    date: "2026-08-23",
    notes: [
      "Aufbauplan erklärt Budget, Ansehen, Einfluss und Level/Rang einzeln — kurz und ohne Details-Overload",
      "Bewerbungsgespräch und eintreffende Bewerber: Fortschrittsbalken im Personal-Panel; Coach sagt: Einsätze parallel weiter bearbeiten",
      "Coach zeigt „Schritt X von Y“ bei Auto-Führung (Besatzung, Fuhrpark, Personal)",
      "Einsatzliste bleibt während Coach-Führung bedienbar — nicht mehr ausgegraut bei HR-/Wachen-Schritten",
      "Spotlight und Kampagnen-Panel optisch aufgeräumt",
    ],
  },

  {
    version: "0.3.39.0",
    date: "2026-08-23",
    notes: [
      "Coach-Führung läuft automatisch: Panels und Tabs öffnen sich von selbst, cyan Ring markiert das nächste Element — kein wiederholtes „Zeigen“ mehr nötig",
      "Lern-Aufträge (Besatzung, Fuhrpark, Personal): Schritt-Hinweis im Coach-Panel, optional „Hinweis wiederholen“",
      "Tablet & Handy: Fahrzeug auf der Karte antippen öffnet Seitenpanel links — Karte und GPS-Route bleiben sichtbar (kein Vollbild-Modal mehr)",
    ],
  },

  {
    version: "0.3.38.0",
    date: "2026-08-23",
    notes: [
      "Starter-Besatzung: Erstes Fahrzeug startet mit voller Mindestbesatzung (LF 6/6) — erster Einsatz sofort alarmierbar",
      "HR-Tutorial: Beim zweiten Fahrzeug an derselben Wache fehlt bewusst 1 Person — Werben und Einstellen lernen",
      "Alte Spielstände: Unterbesetztes Starter-Fahrzeug wird vor der Fuhrpark-Mission automatisch aufgefüllt",
      "Grafik-Pack Münster Berufsfeuerwehr: 90 Fahrzeug-Sprites und 6 Gebäude-Icons (Fan-Referenz), standardmäßig privat unter Profil → Fahrzeug-Optik & Packs",
    ],
  },

  {
    version: "0.3.37.0",
    date: "2026-08-23",
    notes: [
      "Finanzen aus dem unteren Dock — Budget, Ansehen und Einfluss oben rechts öffnen das Finanz-Panel (Kredite, Buchungen)",
      "Verwaltung unten: Betrieb (Wachen, Fahrzeuge, Personal, Bauen) und Bürger & Aufträge (Bürger, Belohnungen)",
      "Top-Leiste: Neuigkeiten als Text neben Wiki/Verband; Tagesauftrag für Login-Bonus und Tagesziele",
      "Ungelesene Neuigkeiten und offene Tagesaufträge mit orangem Punkt markiert",
    ],
  },

  {
    version: "0.3.36.0",
    date: "2026-08-23",
    notes: [
      "Kampagne aus der Top-Leiste und dem Menü entfernt — Story-Auftrag bleibt links im Coach-Panel",
      "Top-Leiste: nur noch Wiki und Verband (Desktop)",
    ],
  },

  {
    version: "0.3.35.0",
    date: "2026-08-23",
    notes: [
      "Top-Leiste aufgeräumt: Wiki, Verband und Kampagne einheitlich — kein extra Katalog-Button mehr",
      "Wiki ist zentraler Hub: Tabs Artikel, FAQ, Einsatzkatalog und Rechtliches (FAQ/Rechtliches nicht mehr im Profilmenü)",
      "Kampagne-Button mit Tooltip — springt zum Story-Auftrag links",
      "Katalog-Editor bleibt im Profilmenü für Nutzer mit Einsatzzugang",
    ],
  },

  {
    version: "0.3.34.0",
    date: "2026-08-23",
    notes: [
      "Coach „Zeigen“ markiert Elemente in Modals jetzt wirklich sichtbar (cyan Ring + Pfeil über dem Fenster)",
      "Neuer Lern-Auftrag: Coach öffnet automatisch Wachen/Personal und führt Schritt für Schritt",
      "Alle Führ-Aufträge: Button heißt „Zeigen“ — Abschluss erst nach echter Aktion (zuweisen, einstellen, kaufen …)",
    ],
  },

  {
    version: "0.3.33.0",
    date: "2026-08-23",
    notes: [
      "Coach „Zeigen“ führt jetzt auch in Modals weiter: Wachen-Liste → Tab → Besatzung/Fahrzeugshop",
      "Personal-Aufträge: Tab Werbung/Bewerber → Einladen/Einstellen werden im Modal hervorgehoben",
      "Coach-Pfeil wartet kurz auf geöffnete Fenster und scrollt zum markierten Element",
    ],
  },

  {
    version: "0.3.32.1",
    date: "2026-08-23",
    notes: [
      "Heimrauchmelder, Zimmerbrand, Wohnhaus-Einbruch u. a.: Einsätze erscheinen nur noch in Siedlungen und Wohngebieten — nicht mehr auf Feldern oder Wiesen",
    ],
  },

  {
    version: "0.3.32.0",
    date: "2026-08-23",
    notes: [
      "Neue Wachen heißen kurz nach Organisation: FW 1, Pol 1, RD 1 … — in der Wachenverwaltung umbenennbar",
      "Wache abreißen: sehr teuer (ca. 250.000 €+), max. 3× pro Spielstand; Leitstelle und letzte operative Wache geschützt",
      "Standort verschieben nach 2 h kostenlos: danach −25.000 € (statt −8.500 €)",
      "Abdeckung/Stadtgebiet: keine gelben Speichen mehr von der Kartenmitte bei unregelmäßigen Stadtgrenzen",
      "Einsätze: nur Wasser-Lagen am Gewässer; normale Einsätze nicht mehr auf dem Wasser",
    ],
  },

  {
    version: "0.3.31.0",
    date: "2026-08-23",
    notes: [
      "Personalwerbung: Werbung schalten → Bewerber warten → Gespräch einladen (~1 Min.) → Eindruck prüfen → annehmen oder ablehnen",
      "Einstellung kostet keine Extra-Gebühr mehr — Werbung war die Budget-Investition, das Gespräch die Zeit",
      "Neuer Start: LF-Besatzung knapp unter Mindeststärke — genau eine Einstellung im Tutorial nötig",
      "Story-Coach führt durch Personal → Werbung → Bewerber → Gespräch → Einstellen",
    ],
  },

  {
    version: "0.3.30.1",
    date: "2026-08-23",
    notes: [
      "Ölspur- und Betriebsstoff-Einsätze erscheinen nur noch an Straßen, Autobahnen oder Tankstellen — nicht mehr mitten auf Feldern",
      "Kurztext passt sich an: z. B. „Ölspur auf der Straße“ oder „… Autobahn“",
    ],
  },

  {
    version: "0.3.30.0",
    date: "2026-08-23",
    notes: [
      "Routenfindung: Fahrzeuge fahren über OSRM entlang der Straßen — Einbahnstraßen und Fahrzeiten werden beachtet",
      "Fallback: Wenn OSRM nicht erreichbar ist, berechnet das Spiel die schnellste legale Strecke aus OSM-Daten statt einer Luftlinie",
      "GPS-Route auf der Karte entspricht der berechneten Anfahrtsstrecke",
    ],
  },

  {
    version: "0.3.29.4",
    date: "2026-08-23",
    notes: [
      "Kampagnen-Bescheide: „Später entscheiden“ entfernt — Verträge und Förderanträge müssen unterschrieben werden",
      "Kein Schließen mehr bei offenen Bescheiden; nach Unterschrift wie bisher mit „Weiter“ schließen",
    ],
  },

  {
    version: "0.3.29.3",
    date: "2026-08-23",
    notes: [
      "Wiki, Verband und Neuigkeiten nur noch oben in der Leiste — unten im Dock bleiben Personal, Wachen, Finanzen & Co.",
      "Verwaltungs-Übersicht ohne Wiki/Verband; Meta-Schaltflächen am Tablet und PC oben links",
    ],
  },

  {
    version: "0.3.29.2",
    date: "2026-08-23",
    notes: [
      "Besatzung: Zuweisung klappt auch für Kräfte außer Dienst; Auto-Befüllung warnt wenn nichts passt",
      "Einsatzliste: Nachalarm und vorgemerkte Fahrzeuge = „in Bearbeitung“, nicht mehr offen/unbearbeitet",
      "Rollen: Einsatzkraft auf dem Besatzungs-Board, Feuerwehrmann/frau in Werbung/Bewerbern",
    ],
  },

  {
    version: "0.3.29.1",
    date: "2026-08-23",
    notes: [
      "Karte: Gelbe Einsatz-Marker nur noch für offene Lagen aus der Einsatz-Liste — abgelaufene Einsätze bleiben nicht mehr auf der Karte",
    ],
  },

  {
    version: "0.3.29.0",
    date: "2026-08-23",
    notes: [
      "Personal: einheitliche Begriffe — Belegschaft, Bewerber, Werbung, Besatzung, freie Kräfte (kein „Pool“/„Personalwesen“ mehr)",
      "Vier klare Schritte im Personal-Hub; Lohn und Statistik direkt in der Belegschaft",
      "Besatzung: „Wache befüllen“, bessere Hinweise und Leerzustände",
      "Wachen-Tab heißt jetzt „Besatzung“ statt „Personal“",
    ],
  },

  {
    version: "0.3.28.0",
    date: "2026-08-23",
    notes: [
      "Verwaltung: Personal, Wachen, Fahrzeuge, Finanzen, Bürger und Belohnungen in einheitlicher Größe — kein Sprung mehr zwischen Vollseite und kleinem Fenster",
      "Personal-Hub: Werbung, Bewerber, Belegschaft und Besatzung an einem Ort; Pool rechts, Fahrzeuge links, Drag oder Klick zum Zuweisen",
      "Rollen-Badges (Vollwerker, Disponent …); früh im Spiel vor allem Vollwerker-Bewerber",
      "Auto-Zuweisung robuster; Wachübersicht verlinkt direkt zur Besatzung",
      "Funk: Vor-Ort-Entscheidungen und Prioritäts-Lagebild; Nachforderungen-Übung in der Kampagne",
    ],
  },

  {
    version: "0.3.27.0",
    date: "2026-08-23",
    notes: [
      "Katalog-Editor: Fahrzeuge, Ausbildungen und Ereignisse ab Start in der gemeinsamen Datenbank – kein Live-Import mehr",
      "Einsatz-Editor: Löschen von Einsätzen schlägt nicht mehr mit Bad Request fehl",
      "Tablet-Layout: Budget, Ansehen und Level oben rechts, Wiki und Verband oben links, Dock unten ohne Überlappung",
      "Oberfläche: Euro, Umlaute und Gedankenstriche werden wieder korrekt angezeigt",
    ],
  },

  {
    version: "0.3.25.0",
    date: "2026-08-23",
    notes: [
      "Kampagne: Polizei vor RD; Landes-Anschub gegen Softlock ohne Geld für die nächste Wache",
      "Wachen-Preise: Polizei günstiger, RD mittel, Feuerwehr etwas teurer; THW später",
      "Abdeckung/Stadtgebiet in der Stats-Leiste; Hydrant-Auffüllfahrt bleibt alarmierbar",
    ],
  },
  {
    version: "0.3.24.0",
    date: "2026-08-23",
    notes: [
      "Katalog-Editor: Einsätze, Fahrzeuge, Ausbildungen und Ereignisse (mit Einsatzzugang)",
      "Besatzung: Personal zuweisen; Alarmierung erst ab Mindestbesatzung",
      "Karte: GPS-Routen bei Anfahrt/Hover; Abdeckung um Leitstelle/Wachen umschaltbar",
      "Story-Kampagne: Aufträge inkl. Personal werben, einstellen und Besatzung",
      "Einsatz-Editor: Economy-Vorschläge; Tutorial-Budget-Failsafe für die Leitstelle",
    ],
  },
  {
    version: "0.3.23.0",
    date: "2026-08-21",
    notes: [
      "Kampagnen-Story: echte Aufträge statt Petition-Spam",
      "Krankenhaus: Betten, Queue, Fachrichtungen, Verlegung",
      "Map: Wachen laden ohne Basemap-Toggle; Radius immer sichtbar",
      "Wachen-Icons als Symbole; Alarmierung UI aufgeräumt; Events seltener",
    ],
  },
  {
    version: "0.3.22.0",
    date: "2026-08-21",
    notes: [
      "Löschwasser: OSM-Siedlung/Industrie → Hydranten; See/Fluss/Kanal als Schlauch-Saugstelle; sonst TLF-Pendel",
      "Skill-Tree 7 Äste inkl. Disposition + Automation; „Nachsorge“ → Nachalarmieren/Anfordern",
      "Karte stärker abgedunkelt (~30%); Konzepte Story/KH + Gesamtninventar dokumentiert",
    ],
  },
  {
    version: "0.3.21.0",
    date: "2026-08-21",
    notes: [
      "Fahrzeugkatalog komplett: GW-L1/L2, Rollcontainer, erweiterte ABs — Shop über Module",
      "Admin → Fahrzeuge: Preis, Besatzung, Schlauch, Wasser/Schaum, Stellplatz-Typ editierbar",
      "RC-Stellplätze an der Wache (Modul Lagerraum / kaufbar)",
    ],
  },
  {
    version: "0.3.20.0",
    date: "2026-08-21",
    notes: [
      "Löschwasser: Stadt (cityRadius) = Hydranten ok; außerhalb/Autobahn = TLF-Wasserpendel (TLF vor Ort, hin/her)",
      "Wachenübersicht Vollseite; Belohnungen (Login/Tages-/Wochenziele) getrennt vom Tutorial-Coach",
      "Admin Late-Game-Boost für Tests (Level 50, Wachen, Fuhrpark)",
    ],
  },
  {
    version: "0.3.19.0",
    date: "2026-08-21",
    notes: [
      "Gebäude-Catalog neu: FW Klein→Groß→Max, Pol Wache→Präsidium, Leitstelle→Stabraum; Werks-/Flughafen-FW, BePo, Heli, JVA, KH, RTH, RHS, Berg/Wasser/Seenot",
      "Module (KDD/SEK/MEK, WLF+AB, THW-Fachgruppen …) + Stellplätze Fahrzeug/Anhänger/AB",
      "Fahrzeug-Phase-1 inkl. KDD, BAB-FuStW, WLF/AB — ohne SEG/FF; Kripo nur am Präsidium",
    ],
  },
  {
    version: "0.3.18.0",
    date: "2026-08-21",
    notes: [
      "Tutorial komplett neu: Reset startet bei Schritt 1, Gates blockieren Weiter, Coach einklappbar zum Icon",
      "Einsatzansicht groß: Lage/Anfahrt/Wasser/Nachalarmierung; Alarmierung nur dort",
      "Wachenverwaltung fullscreen; Dock Wachen/Fahrzeuge/Personal; Fuhrpark-Übersicht",
      "Rechte Spalte breiter; Fahrzeuge aus der Einsatzliste entfernt",
    ],
  },
  {
    version: "0.3.17.0",
    date: "2026-08-21",
    notes: [
      "Tutorial: kein Zurückwerfen zum ersten Einsatz; Einzelschritte mit blauem Rahmen",
      "Karte: Wachen + parkende Fahrzeuge sichtbar (auch nach Satellit/Straße)",
      "Wasser/Schlauch im Tutorial nur als Tipp",
      "Admin-Katalog: Dropdown-Editor für Bedarf, Gates, Ausbreitung, Flags",
    ],
  },
  {
    version: "0.3.16.0",
    date: "2026-08-21",
    notes: [
      "Wiki-Einsätze: Polizei/Wasser/Autobahn nicht mehr fälschlich mit LF",
      "Gates & Bedarf aus Einsatzart + Wiki-PDF (Geldtransport→FuStW, Badestelle→Boot, …)",
      "Einsatzausbreitung (expandTo) + eigene Einsätze (Werttransport, Geisterfahrer, …)",
    ],
  },
  {
    version: "0.3.15.0",
    date: "2026-08-21",
    notes: [
      "Registrierung: klare Fehlermeldungen statt 500; Benutzernamen mit Punkt/Leerzeichen",
      "Einsatz-Cap hart: kein Spawn über Level-Maximum",
      "Nachsorge-Tab entfernt — Anforderungen in Status/Sprechwunsch",
      "Tutorial: großer Ziel-Pfeil, klarere Schritte, Katalog → Tagesziele",
    ],
  },
  {
    version: "0.3.14.0",
    date: "2026-08-21",
    notes: [
      "E-Mail-Login/Registrierung, playAllowed-Freigabe, Admin-Rollen",
      "Admin-Konsole: User freigeben, Passwort setzen, Katalog reviewed/persistent",
      "Seed-Import merge / reset_unreviewed / force — Edits überleben Deploys",
      "Druck-Einsätze: Bedarf über Fuhrpark möglich (⚠ Fehlbestand + Funk-Hinweis)",
      "Legal/DSGVO-Texte für geschlossene Testphase aktualisiert",
    ],
  },
  {
    version: "0.3.13.0",
    date: "2026-08-21",
    notes: [
      "Tutorial neu: Startorganisation FW/Pol/RD wählen, bis Level 2 geführt mit Spotlight",
      "Einsätze/Nachforderungen an Fuhrpark + Tutorial gekoppelt (kein Notarzt ohne NEF)",
      "Unteres Dock: Wachen/Fahrzeuge/Personal-Übersicht + Katalog-Shortcut",
      "Eigene Seite Einsatzkatalog mit Level, Gates und Bedarf",
    ],
  },
  {
    version: "0.3.12.0",
    date: "2026-08-21",
    notes: [
      "Autobahn-Einsätze nur noch auf echter BAB-Geometrie (OSM roads) — kein Stadt-Fallback",
      "Wiki-Einsätze mit Ortsbindung (Autobahn/Schule/…); Spawn lädt roads+buildings",
      "Einsatz-Modal: Lage/Bedarf, Alarmierung, Funk&Nachsorge als Sprechwunsch/Anruf",
      "Wachen-Übersicht nach Org gruppiert mit Fuhrpark-/Personalstatus",
    ],
  },
  {
    version: "0.3.11.2",
    date: "2026-08-21",
    notes: [
      "Reset-Fix: radio_calls bei Weltlöschung (FOREIGN KEY)",
      "Admin: Tab „Einsatzkatalog“ mit Suche/Filter/Spawn vorne",
    ],
  },
  {
    version: "0.3.11.1",
    date: "2026-08-21",
    notes: [
      "Ausrüstung typgerecht: FuStW/RTW ohne Wassertank/Schaum; Polizei Funk+Handschellen, RD Medizin, THW Pumpe",
      "UI zeigt nur passende Meter; Snapshot normalisiert Alt-Daten",
    ],
  },
  {
    version: "0.3.11.0",
    date: "2026-08-21",
    notes: [
      "~400 Wiki-Einsätze (leitstellenspiel.de) mit Wachen-Gates + Cap nach Wachen/Level",
      "Unwetter/Sperrung: kein Zimmerbrand mehr; THW-Sturm nur mit THW, sonst Baum auf Straße",
      "Funk-Tabs (Anrufe/Sprechwunsch/Status/Nachsorge); Bürger-Panel mit Plan-Einsatz Schule",
      "Nachsorge differenziert (Absperren, Umleitung, Ampel, Reinigung); Wasserpendel nur bei Bedarf",
      "Kompetenzbaum als Zweige (Alarmierung 5 Stufen); Alarm-Panel nach Wache + AAO",
      "Admin-Katalog zeigt Gates; Tick-Endpoint für Bürger-Spawn",
    ],
  },
  {
    version: "0.3.10.0",
    date: "2026-08-21",
    notes: [
      "Abschlepphof & Bauhof baubar — Nachsorge aus eigener Tasche günstiger",
      "Kompetenzbaum +4 Knoten (ETA-Hilfe, Patientenfluss, Logistik, Pendel-Taktik)",
      "Wasserpendel: bis 3 Hydrant↔ES-Zyklen; Nachsorge-Status am Einsatz",
    ],
  },
  {
    version: "0.3.9.0",
    date: "2026-08-21",
    notes: [
      "Funk: Öffnen / Erledigt / Abschlepper / Wasserpendel / Gefangene übernehmen",
      "Kompetenzbaum-Fundament (Bedarf vorschlagen, Funk-Makros, Auto-Wartung, Schlauch)",
      "Kredit levelgekoppelt; Wasserpendel mit TLF→Hydrant; Patient→KH auto",
      "Lageboard mit Kräftebedarf; Einsatz-Wasserlage",
    ],
  },
  {
    version: "0.3.8.0",
    date: "2026-08-21",
    notes: [
      "Löschwasser: Siedlungs-Hydranten (Raster) statt Massen-OSM; Land/Autobahn → Pendeln",
      "Nach Brand: Tank am Hydranten wieder voll; an der Wache immer auffüllen",
      "Schlauchlänge entscheidet Reichweite; Status-5-Hinweise bei Wasserproblemen",
    ],
  },
  {
    version: "0.3.7.1",
    date: "2026-08-21",
    notes: [
      "Einsatzgebiet: Abdeckung um Leitstelle/Wachen wächst — max. Stadt-/Kreisgrenze",
      "Spawn/Bau nur in Abdeckung; Fahrzeuge dürfen darüber hinaus",
    ],
  },
  {
    version: "0.3.7.0",
    date: "2026-08-21",
    notes: [
      "Spielgebiet: Stadt / Landkreis / freie Region wählen",
      "Landing unter / , Spiel unter /play",
      "Funk: Status 5, Sprechwünsche, Anrufe — klickbar + Ton, kein Anfahrt-Spam",
      "Tutorial Ansehen vs. Einfluss klarer",
    ],
  },
  {
    version: "0.3.3.1",
    date: "2026-08-20",
    notes: [
      "Fahrzeugbilder in Fuhrpark-Übersicht & Disposition; Größen-Templates S/M/L/XL",
      "Einsatzgrößen an Level, Fuhrpark und Sinnhaftigkeit gekoppelt",
      "Admin-Panel: Kennzahlen, Katalog, Einsatz-Spawn",
      "Auto-Wiki aus Katalog; Budget/Ansehen/Einfluss klickbar mit Tagesstatistik",
      "Feinere Changelog-Versionierung (0.3.3.x → Ziel ~0.7 Alpha)",
    ],
  },
  {
    version: "0.3.3.0",
    date: "2026-08-20",
    notes: [
      "Satnav/Split + Fahrzeug-Sprites, Blaulicht, User-Packs",
      "Rechtliches (Platzhalter)",
    ],
  },
  {
    version: "0.3.0",
    date: "2026-08-20",
    notes: [
      "BOS-Leitstelle: Bootstrap-Radius → Stadtgebiet nach Bau/Anbau an Großwache",
      "Bau-Menü statt Dauer-Bauleiste; Wachen-Dock unten zum Durchklicken",
      "Fahrzeug: Verschieben, Wartung (braucht Mechaniker), Tank/Schaum befüllen",
      "Level/XP + Random-Events: Straßensperrung, Unwetter, Großereignis, VIP",
      "Neue Gebäude: Präsidium, Schulen, FW-Werkstatt",
    ],
  },
  {
    version: "0.2.0",
    date: "2026-08-20",
    notes: [
      "FF, RD, THW als Organisationen",
      "OSM-Claim echter Wachen (−45 % Baukosten)",
      "Bau-Radius wächst mit Wachen/Ansehen",
      "Dienstplan (Dienst / frei)",
      "Einsatz-Phasen + Restzeiten, Personal-Werbung",
    ],
  },
  {
    version: "0.1.1",
    date: "2026-08-20",
    notes: ["OSRM-Straßenrouting", "POI-Layer", "Wachen-Modal", "Alarm-Sounds"],
  },
  {
    version: "0.1.0",
    date: "2026-08-20",
    notes: ["Local Alpha: Stadt, BF-Kleinwache, Disposition, Solo-Welt"],
  },
];

export const FAQ = [
  {
    q: "Wie starte ich?",
    a: "Stadt wählen → Startorganisation → erste Wache im Startgebiet bauen. Story/Tutorial führt dich zu Leitstelle, Fuhrpark und Personal. Details: Neuigkeiten in der Top-Leiste.",
  },
  {
    q: "Wozu die Leitstelle?",
    a: "Ohne Leitstelle nur kleiner Start-Radius. Mit Leitstelle (separat oder an BF-Großwache) aktivierst du Stadtgebiet und wachsende Abdeckung um deine Wachen.",
  },
  {
    q: "Warum kann ich nicht alarmieren?",
    a: "Fahrzeug braucht zugewiesene Besatzung bis zur Mindeststärke (Wache → Fuhrpark/Besatzung oder Personal). Leere Sitze = kein Alarm.",
  },
  {
    q: "Was ist die Abdeckung auf der Karte?",
    a: "Blauer Bereich: wo Einsätze spawnen und du bauen darfst (um Leitstelle/Wachen). Umschalter in der Karten-UI. Gelb = Stadt-/Kreisgrenze. Teal = Nachbarkreis (gleiche Karte; Spawn/Bau/Alarm ab Level 12 Interim).",
  },
  {
    q: "Was bedeutet „1 Spielstand / Slot“?",
    a: "Default: ein Slot = eine Stadt, ein Fortschritt. Admin kann das Limit anheben — dann erscheint ein Slot-Picker (anlegen, wechseln, löschen). Ressourcen bleiben getrennt. Siehe Neuigkeiten 0.4.29.",
  },
  {
    q: "Was ist ein OSM-Claim?",
    a: "Echte Feuerwachen/Polizei/Kliniken von der Karte übernehmen (−45 %). Im Bau-Menü bei FW/Pol/RD „OSM Claim“, dann OSM-Marke anklicken.",
  },
  {
    q: "Wie warte ich Fahrzeuge?",
    a: "Zustand sinkt nach Einsätzen. Im Fuhrpark „Wartung“ — braucht freien Mechaniker/Maschinisten (Werkstatt empfohlen). „Befüllen“ nur bei Löschfahrzeugen (Tank/Schaum); Polizei/RD frischen an der Wache auf.",
  },
  {
    q: "Was sind Lagen/Events?",
    a: "Ab Level 2: Straßensperrungen & Co. verlangsamen Anfahrten. Panel „Lagen“ unten. Großereignisse spawnen schwere Einsätze.",
  },
  {
    q: "Bürger-Anfragen?",
    a: "Panel „Bürger“: Petitionen und Beschwerden. Zusagen kostet Budget und gibt Ansehen/Einfluss; Ablehnen kostet Renommee. Story-Aufträge laufen parallel über die Kampagne.",
  },
  {
    q: "Wie wird die Großwache?",
    a: "BF-Kleinwache öffnen → Zur Großwache ausbauen. Optional Leitstelle dort anbauen.",
  },
  {
    q: "Budget / Ansehen / Einfluss?",
    a: "Oben anklicken → Statistik. Ansehen: Image-Kampagne unter Finanzen → Ansehen (Bewerber). Einfluss: Förderung/Rathaus und Kompetenzbaum. Autopilot-Überhang kostet Ansehen.",
  },
  {
    q: "Welche Tastaturkurzbefehle gibt es?",
    a: "Q = obersten Funk öffnen/quittieren · A = Disposition/Alarm · D = AAO Maximalbedarf · 1–9 = n-ten Einsatz der gefilterten Liste · ? = Kurzhilfe · Esc = Modal schließen.",
  },
  {
    q: "Wann gibt es ein Update?",
    a: "Neuigkeiten in der Top-Leiste listen freigegebene Versionen. Schema 0.3.x.y; Ziel Closed Alpha/Beta ~0.7. Ausführlicheres Repo-Changelog fürs Team.",
  },
];

export const WIKI = [
  {
    id: "wiki-folgt",
    title: "Wiki folgt",
    body: "Ausführliche Wiki-Artikel werden später poliert. Bis dahin: Neuigkeiten (Changelog), Suche, FAQ-Tab, Einsatzkatalog-Tab und Auto-Wiki aus dem Spielkatalog.",
  },
  {
    id: "release",
    title: "Release & Updates",
    body: "Schema 0.MAJOR.MINOR.PATCH. Feature-Arbeit in 0.3.x.y; Ziel Closed Alpha/Beta ~0.7. Zwischenstände unter „In Arbeit“. Team-Quelle: CHANGELOG.md im Repo.",
  },
  {
    id: "crew",
    title: "Personal & Besatzung",
    body: "Personal werben und einstellen (HR / Wache). Jedem Fahrzeug Besatzung zuweisen. Unter Mindestbesatzung kein Alarm. „Wache befüllen“ und „Dienstplan sichern“ (Frei→Dienst + Mindeststärke). Image-Kampagne (Finanzen→Ansehen) bringt bessere Bewerber.",
  },
  {
    id: "coverage",
    title: "Abdeckung & GPS",
    body: "Abdeckung wächst mit Leitstelle und Wachen — Spawn und Bau nur darin. Umschalter zeigt Abdeckung oder nur Stadtgrenze. Gelb = Heimat, teal = Nachbarkreis (gleiche Karte). GPS-Linien: Anfahrt bei Hover/Detail.",
  },
  {
    id: "nachbarkreis",
    title: "Nachbarkreis (Wiki stub)",
    body: "Wiki folgt. Kurz: genau ein Nachbargebiet neben der Stadtgrenze auf derselben Karte. Ab Level 12 (Interim) oder Story: Spawn, Cross-Alarm und Bau. Skills bleiben; Wachen im Nachbar starten von null. Kartenlabel zeigt gesperrt/aktiv. Bahn-Transfer: Terminals in beiden Zonen. Details: Neuigkeiten 0.4.27.",
  },
  {
    id: "jva",
    title: "JVA / Justiz (Wiki stub)",
    body: "Wiki folgt. Kurz: JVA ab Level 16 mit Polizei und Gewahrsam-Erfahrung (3× übernehmen oder Kompetenz). Zellen-Überlauf der Polizei geht in die JVA; manuell „→ JVA“ in der Polizei-Wache. In der JVA: Gerichtstermin-Stub oder Entlassen. Tageslage zeigt Haftplätze. Echter Fahrzeug-Transport später. Siehe Neuigkeiten 0.4.29.",
  },
  {
    id: "spielstand",
    title: "Spielstand / Slot",
    body: "Ein Slot = ein Speicherplatz (eine Stadt), Ressourcen getrennt. Default 1 Slot; Admin/Env können Limit anheben — dann Slot-Picker in der Stadtauswahl. Siehe Neuigkeiten 0.4.29.",
  },
  {
    id: "story",
    title: "Story-Kampagne",
    body: "Aufbau: Leitstelle → Wache → Besatzung → Alarm → Personal → Funk. Karten-Taktik nach Flächendeckung (Kap. 2). Kap. 4 Nachbarkreis (Lv12); Kap. 5 Nachbar-Wache, Bahn, dann JVA; Kap. 6 Landesnetz. Semi-Auto ab Lv8, Autopilot ab Lv14. Details: Neuigkeiten 0.5.0.",
  },
  {
    id: "katalog-editor",
    title: "Katalog-Editor",
    body: "Nur mit Recht „Einsatzzugang“: Einsätze, Fahrzeuge, Ausbildungen, Ereignisse editieren. Vorschläge für Bedarf/Economy; Review-Queue. Spieler ohne Recht sehen den Menüpunkt nicht nutzbar.",
  },
  {
    id: "finance",
    title: "Finanzen",
    body: "Budget, Ansehen und Einfluss sind klickbar. Ledger speichert jede Buchung. Ansehen: Image-Kampagne für Bewerber (Softcap ab 100). Einfluss: Förderung und Kompetenzen.",
  },
  {
    id: "admin",
    title: "Admin",
    body: "Menü Admin: Kennzahlen, Spawn, Katalog, User-Freigabe, Modul-Rechte (u. a. Einsatzzugang). Owner der Solo-Welt bzw. freigegebene Admins.",
  },
  {
    id: "incident-scale",
    title: "Einsatzgrößen",
    body: "klein→Lage gekoppelt an Level und Fuhrpark. Ohne passende Fahrzeuge/Orgs keine unsinnigen Großlagen.",
  },
  {
    id: "training-atemschutz",
    title: "Atemschutzgeräteträger",
    body:
      "Qualifikation für Atemschutz-Einsätze (Tunnel, Industrie, Kellerbrand, Qualm, Innenangriff). " +
      "Gate im Einsatz-Banner blockiert Alarmierung ohne passendes Personal — nicht nur langsamer. " +
      "Freischaltung: Personal → Lehrgang „Atemschutz“ oder Feuerwehrmann/Maschinist mit Können ≥58. " +
      "Betrifft u. a. DLK, GW-Atemschutz und Brandlagen mit Rauch/Atemschutz-Bedarf. " +
      "Ohne Träger: Bedarf bleibt offen, Fortschritt stockt, ggf. Fehlbestand-Druck. " +
      "Tipp: Mehrere Träger an der Hauptwache halten — Großlagen brauchen Reserve.",
  },
  {
    id: "training-boot",
    title: "Bootsführer / Wasserrettung",
    body: "Bootsführer für Seenot-, Kanal- und Wasserrettungseinsätze. Gate bei Boot/GW-Wasser im Bedarf. Qualifikation: Boot-Crew oder erfahrener Feuerwehrmann (Lehrgang Bootsführer). Ohne Gate: nur Landeinsätze — Wasserlagen spawnen seltener bis Modul/Wache steht.",
  },
  {
    id: "training-gf",
    title: "Gruppenführer / ELW",
    body:
      "Gruppenführer, Disponent oder sehr erfahrener Feuerwehrmann (Können ≥72) für Führungs- und Taktik-Gates. " +
      "Kompetenz gf_kleinlagen: ELW/Gruppenführer vor Ort entscheidet Standard-Taktik auf Kleinlagen (Chip GF/ELW). " +
      "Großlagen: Abschnittsführung über Stabsraum/Karte (Sammelstelle, Lageboard). " +
      "Ohne GF: manche Taktik-Angebote und Führungslagen bleiben gesperrt oder teurer in Zeit.",
  },
  {
    id: "training-rd",
    title: "Notfallsanitäter / RD",
    body:
      "Mindestqualifikation für RTW, NEF und MANV-Gates. Sanitäter mit Können ≥60 oder abgeschlossener Lehrgang. " +
      "MANV-Einsätze verlangen oft zusätzlich Gruppenführer und Patienten-Sammelstelle auf der Karte (Stabs Phase 2/3). " +
      "Triage lite (Rot/Gelb/Grün) im Einsatz beschleunigt den Fortschritt; Transport/Behandlung bleibt als Fallback. " +
      "KH-Hub: volle Betten/Warteschlange → Module nachrüsten oder verlegen.",
  },
  {
    id: "training-pol",
    title: "Polizei-Einsatztraining",
    body:
      "Grundqualifikation für FuStW, BAB-Streifen und Speziallagen. Officer-Rolle + Lehrgang oder Können ≥55. " +
      "Spezial-Einsätze (Geldtransport, Observation, SEK/MEK) können höhere Pol-Wachen-Gates und Module verlangen — siehe Einsatzkatalog. " +
      "Gewahrsam: nach Festnahme im Funk Zelle/Übernehmen — Story Kap. 2 trainiert das.",
  },
  {
    id: "training-maschinist",
    title: "Maschinist / Fahrer schwer",
    body:
      "Maschinist (Apparatus Driver, Können ≥52) für DLK, TLF, HLF und schwere Fahrzeuge — nicht für Basis-LF im Frühspiel. " +
      "Lehrgangs-Gates sind soft bis Level 5 und Abschluss der ch1-Personal-/Fuhrpark-Kette; danach blockiert fehlender Maschinist die Alarmierung. " +
      "Gate-Chip im Banner nennt fehlende Qualifikation — Wiki-Link führt hierher. " +
      "Lehrgang an Feuerwehrschule in Reichweite kann rabattiert sein (Kompetenz/Schule).",
  },
  {
    id: "training-disponent",
    title: "Disponent Leitstelle",
    body: "Leitstellen-Disponent (Können ≥55) deckt Führungs-Gates ab und verbessert Sprechwunsch-Priorität. Später: Auto-Alarmierung (Kompetenzbaum Alarmierung L5–L20). Personal mit dispatcher-Rolle an ILS oder Wache zuweisen.",
  },
  {
    id: "training-thw",
    title: "THW-Grundqualifikation",
    body: "THW-Helfer (Können ≥45) für THW-Fahrzeuge und technische Hilfe. NEA-, Hochwasser- und Bergungslagen. THW-Wache oder Modul nötig — reine FW-Wache deckt THW-Bedarf nicht ab.",
  },
  {
    id: "einsatz-gates",
    title: "Einsatz-Gates (Überblick)",
    body:
      "Drei Ebenen: (1) Wachen-Gates — genug FW/Pol/RD/THW-Wachen in der Region. " +
      "(2) Fahrzeug-Gates — passende Typen im Fuhrpark (sonst Fehlbestand/Druck und Story-Dialoge). " +
      "(3) Personal-Gates — Lehrgänge/Rollen am Einsatz (Chip im Banner + Wiki). " +
      "Frühspiel-Grace: Lehrgangs-Gates soft bis Level 5 und ch1 Personal/Fuhrpark; Basis-LF ohne Maschinist. " +
      "Runtime-Inferenz aus Katalog; Editor-Entwürfe ersetzen Live-Seed nicht automatisch. " +
      "Autopilot/Semi-Auto haben Parallel-Caps und Soft-Malus bei zu hohem Auto-Anteil — manuell alarmieren bleibt wichtig.",
  },
  {
    id: "sprites",
    title: "Fahrzeug-Optik",
    body: "Templates S/M/L/XL für Sprite-Größen. Eigene Bilder unter Optik; Packs lokal + Freigabe-Stub. Grafikpack: pro Typ zwei Dateien — {typ}.png (Wache/idle) und {typ}-bl.png (Blaulicht bei Einsatz). Anleitung: public/packs/README.md",
  },
  {
    id: "legal",
    title: "Rechtliches",
    body: "Impressum, Datenschutz, AGB und Jugendschutz im Wiki-Tab „Rechtliches“. Vor öffentlichem Betrieb Angaben ersetzen und prüfen lassen — siehe LEGAL.md.",
  },
  {
    id: "buerger",
    title: "Bürger-Anfragen",
    body: "Politische und zivile Entscheidungen: Zusagen bindet Budget, steigert Ansehen/Einfluss. Ablehnen spart Geld, kostet Image.",
  },
  {
    id: "umbau",
    title: "Fahrzeug-Umbau",
    body: "LF→HLF, TSF→LF, RTW→NEF. Braucht Werkstatt oder freien Mechaniker/Maschinisten. Rufname wird angepasst.",
  },
  {
    id: "leitstelle",
    title: "BOS-Leitstelle",
    body: "Zentrale Disposition. Schaltet Stadtgebiet und Abdeckungs-Wachstum frei. Bau unter Führung oder Anbau an BF-Großwache.",
  },
  {
    id: "wartung",
    title: "Fahrzeugwartung",
    body: "Zustand sinkt mit Einsätzen und verschlechtert die ETA. Wartung kostet Budget und bindet Mechaniker für kurze Zeit.",
  },
  {
    id: "events",
    title: "Sonderlagen",
    body: "Level-basiert: Straßensperrung, Unwetter, Großereignis, VIP. Beeinflussen Anfahrt und spawnen teils Zusatzlagen. Admins: Ereignis-Tab im Katalog-Editor.",
  },
  {
    id: "welt-ereignisse",
    title: "Welt-Ereignisse",
    body:
      "Aus dem Live-Katalog (Straßensperrung/Baustelle, Unwetter, Großereignis …) und optional Ereignis-Editor (Status ready). " +
      "Bürgeranrufe/Petitionen laufen über Panel Bürger. Seed-Beispiele: Baustellenbereich, Sperrung Baum/Hindernis, BAB-Engstelle — siehe Auto-Wiki.",
  },
  {
    id: "spezial-fahrzeuge",
    title: "Spezialfahrzeuge (Kripo · KTU · NEA · Pol-Heli)",
    body:
      "KripoVan und KTU: Pol-Spezial unter Einsatz → Anfordern. NEA50/NEA200: Modul fw_nea an Hauptwache (ersetzt Zivil-NEA). PolHeli: Hubschrauber — Luftführung bei Verfolgung. Auto-Wiki listet Preise und Mindestbesatzung.",
  },
  {
    id: "claim",
    title: "OSM-Claim",
    body: "Übernimmt einen realen OSM-Standort als eigene Wache mit Rabatt. Alternativ: Als real markieren (−15 %).",
  },
  {
    id: "verband",
    title: "Verband (geplant)",
    body: "Später: kooperativer Multiplayer — lokale Amtshilfe, gemeinsame Großlagen, Bahn-Transfer (kein Teleport, kein HQ). Join-Modus und Name speichern sich schon im Stub. Solo-Vorschau: Bahn Heimat↔Nachbar. Siehe Neuigkeiten 0.4.30.",
  },
  {
    id: "bahn_transfer",
    title: "Bahn-Transfer (Wiki stub)",
    body: "Fahrzeuge per Bahn verlegen: Güterbahnhof oder Modul Bahnanschluss in Heimat und Nachbarkreis. „Bahn…“ nur andere Zone inkl. ETA. Auf der Karte wandert der Marker Terminal→Terminal. Kein Teleport. Später auch zwischen Verbandsmitgliedern.",
  },
  {
    id: "saison",
    title: "Saison-Events (Wiki stub)",
    body: "Kalender-Saison: Banner (klickbar → Ranking-Board), Bonus beim Abschluss, Spawn-Bias, Solo-Punkte. Admin kann Saison-Vorschau setzen. Verbands-Leaderboard später. Details: Neuigkeiten 0.4.29/0.4.30.",
  },
];

export function searchMeta(query: string) {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [] as Array<{ kind: string; title: string; body: string }>;
  const hits: Array<{ kind: string; title: string; body: string }> = [];
  for (const f of FAQ) {
    if (f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)) {
      hits.push({ kind: "FAQ", title: f.q, body: f.a });
    }
  }
  for (const w of WIKI) {
    if (w.title.toLowerCase().includes(q) || w.body.toLowerCase().includes(q) || w.id.includes(q)) {
      hits.push({ kind: "Wiki", title: w.title, body: w.body });
    }
  }
  for (const note of WIP_NOTES) {
    if (note.toLowerCase().includes(q)) {
      hits.push({ kind: "WIP", title: "In Arbeit", body: note });
    }
  }
  return hits;
}
